import React from 'react';
import { get, without, isEmpty } from 'lodash-es';
import { getModelState } from 'client/data/luckdragon/model';
import { ModelPreloader } from 'client/data/luckdragon/redux/model-preloader';
import { BuildPriceModel, BuildPricePaths } from 'client/data/models/build-price';
import {
  buildNoSubmodelVehiclePath,
  buildPopularStylesPath,
  buildSubmodelYearsPath,
  buildTransactionCountPath,
  VehicleModel,
} from 'client/data/models/vehicle-v2';
import { NativeAdsCreativeConfigModel } from 'client/data/models/native-ads-creative-config';
import { getUnifiedLeadFormDealerCountV2Path, UnifiedLeadFormModel } from 'client/data/models/unified-lead-form';
import { buildSimilarVehiclesCrossShopPath, CoreResearchModel } from 'client/data/models/core-research';
import { buildMakeModelInventoryCountPath, InventoryAggregateModel } from 'client/data/models/inventory-aggregate';
import { RandomModel, RandomPaths } from 'client/data/models/random';
import { getVehicleFromSubModels, updateVehicle } from 'client/actions/vehicle';
import { setCriticalCss } from 'client/actions/critical-css';
import { setVenomTestHeader } from 'client/actions/venom-test-header';
import { getQuery } from 'client/utils/location';
import { getParamsWithMakes } from 'client/utils/router-params';
import { pageDefinition } from 'site-modules/shared/pages/page';
import { makeNiceName } from 'site-modules/shared/utils/nice-name';
import { handle404Error, handle500Error } from 'site-modules/shared/utils/errors';
import { getBreadcrumbSchema } from 'client/seo/schemas/breadcrumbs';
import { logger } from 'client/utils/logger/logger';
import {
  getCreativeConfigAdPathByVehicle,
  setAdsListToPreload,
  siteServedAdsCriticalCss,
} from 'site-modules/shared/components/native-ad/utils/utils';
import { EdmundsCpaNoAdhesionDecorator } from 'site-modules/shared/pages/decorators/edmunds-cpa-no-adhesion';
import {
  buildBreadcrumbs,
  getStyleFilterFromQuery,
  getModelYearById,
  getModelYearByYear,
  getStepCriticalCss,
  handleFaviconQuery,
} from 'site-modules/shared/utils/build-price/build-price';
import { BARS_PER_DEAL, STEP_FILTERS, STEP_NUMBER } from 'site-modules/shared/constants/build-price';
import { PageModel } from 'client/data/models/page';
import { LEAD_FORM_CONFIGS } from 'site-modules/shared/constants/lead-form/lead-form-configs';
import { decoratorParams } from 'site-modules/shared/pages/decorators/decorator-params';
import { INCENTIVES_AD } from 'site-modules/shared/components/native-ad/utils/constants';
import { BuildPrice } from './build-price';

const PAGE_NAME = 'new_model_core_build_and_price';
const PAGE_CATEGORY = 'new_model_core';
const PRELOAD_STAGE = {
  MODEL_YEAR_UNVALIDATED: 'model_year_unvalidated',
  MODEL_YEAR_VALIDATED: 'model_year_validated',
};
const STEP_TITLE_MAP = {
  [STEP_NUMBER.STEP_3]: ' | Customize',
  [STEP_NUMBER.STEP_4]: ' | Price',
  [STEP_NUMBER.STEP_5]: ' | Quote',
};

export const pageOptions = {
  name: PAGE_NAME,
  category: PAGE_CATEGORY,
  preload: (store, props) => {
    const params = getParamsWithMakes(props);
    const query = handleFaviconQuery(getQuery(props.location));
    const { mobile } = store.getState();
    const styleFilter = getStyleFilterFromQuery(query);
    const buildPriceFilter = {
      styleFilter,
      styleId: get(query, 'styleId'),
      optionIds: get(query, 'optionIds', ''),
      urlOptionIds: get(query, 'optionIds', ''),
      defaultValue: get(query, 'defaultValue', ''), // used for back-link logic
      step: parseFloat(
        get(
          STEP_FILTERS.find(({ filters }) =>
            filters.every(filter => Object.prototype.hasOwnProperty.call(query, filter))
          ),
          'step',
          STEP_NUMBER.STEP_1
        ),
        10
      ),
    };
    const modelyearId = styleFilter.modelyearId || buildPriceFilter.defaultValue.split(',')[0];
    let preloadStage = PRELOAD_STAGE.MODEL_YEAR_UNVALIDATED;

    store.dispatch(
      setVenomTestHeader({
        step: buildPriceFilter.step,
      })
    );

    const preloader = new ModelPreloader(store);

    preloader.resolve(BuildPricePaths.buildModelYearsPath(params), BuildPriceModel);
    preloader.resolve(buildSubmodelYearsPath(params), VehicleModel);

    if (!(styleFilter.modelyearId && styleFilter.submodel)) {
      // STEP 1
      if (!buildPriceFilter.defaultValue) {
        preloader.resolve(BuildPricePaths.buildMakeModelDefaultYear(params), BuildPriceModel);
      }
      preloader.resolve(BuildPricePaths.buildYearsSubmodelsPath(params), BuildPriceModel);
    }

    return preloader
      .load()
      .then(modelState => {
        const modelYears = modelState.get(BuildPricePaths.buildModelYearsPath(params), BuildPriceModel);
        // 2nd option works only on STEP 1
        const currentModelYear =
          !!modelYears &&
          (modelyearId
            ? getModelYearById(modelYears, modelyearId)
            : getModelYearByYear(
                modelYears,
                modelState.get(BuildPricePaths.buildMakeModelDefaultYear(params), BuildPriceModel)
              ));

        if (isEmpty(currentModelYear)) {
          throw handle404Error({ errorMessage: 'There are no new, new_used submodels for selected year' });
        }

        preloadStage = PRELOAD_STAGE.MODEL_YEAR_VALIDATED;

        const subModels = modelState.get(buildSubmodelYearsPath(params), VehicleModel);
        const subModelNiceName = makeNiceName(styleFilter.submodel);
        const subModelName = get(subModels, `${subModelNiceName}.name`, '');
        const subModelId = get(subModels, `${subModelNiceName}.years[${currentModelYear.year}].id`);

        const vehicleContext = getVehicleFromSubModels(
          subModelNiceName,
          currentModelYear,
          subModels,
          modelyearId,
          buildPriceFilter.styleId
        );
        store.dispatch(
          updateVehicle(
            vehicleContext || {
              make: { niceName: params.make },
              model: { niceName: params.model },
              modelYear: { year: currentModelYear.year, id: Number(modelyearId || currentModelYear.id) },
              subModel: {
                name: subModelName,
                niceName: subModelNiceName,
                id: subModelId,
              },
              ...(buildPriceFilter.styleId && {
                style: {
                  id: Number(buildPriceFilter.styleId),
                },
              }),
            }
          )
        );

        // related links, STEP 3, 4
        preloader.resolve(
          buildPopularStylesPath({ make: params.make, model: params.model, year: currentModelYear.year }),
          VehicleModel
        );
        // related links
        preloader.resolve(
          buildSimilarVehiclesCrossShopPath(get(vehicleContext, 'model.modelLinkCode'), 6),
          CoreResearchModel
        );

        // for build steps menu
        if (!buildPriceFilter.styleId) {
          preloader.resolve(
            buildNoSubmodelVehiclePath({ make: params.make, model: params.model, year: currentModelYear.year }),
            VehicleModel
          );
        }

        // STEP 1
        if (buildPriceFilter.step === STEP_NUMBER.STEP_1) {
          buildPriceFilter.styleFilter.modelyearId = currentModelYear.id;
        }

        // STEP 2.5
        if (buildPriceFilter.step === STEP_NUMBER.STEP_2_5) {
          preloader.resolve(
            BuildPricePaths.buildStylesListPath({
              make: params.make,
              model: params.model,
              submodel: makeNiceName(buildPriceFilter.styleFilter.submodel),
              year: currentModelYear.year,
            }),
            BuildPriceModel
          );
        }

        if (buildPriceFilter.styleId) {
          // STEP 3, 4
          preloader.resolve(BuildPricePaths.buildStyleOptionsPath(buildPriceFilter.styleId), BuildPriceModel);
          preloader.resolve(
            BuildPricePaths.buildConfigurationWithOptionsPath(buildPriceFilter.styleId, buildPriceFilter.optionIds),
            BuildPriceModel
          );
          preloader.resolve(
            BuildPricePaths.buildColorPhotosPath({
              make: params.make,
              model: params.model,
              year: currentModelYear.year,
              submodel: `${currentModelYear.modelName} ${buildPriceFilter.styleFilter.submodel}`,
            }),
            BuildPriceModel
          );

          if (buildPriceFilter.step === STEP_NUMBER.STEP_4) {
            // STEP 4
            preloader.resolve(
              buildMakeModelInventoryCountPath({ make: params.make, model: params.model }),
              InventoryAggregateModel
            );
            preloader.resolve(buildTransactionCountPath(buildPriceFilter.styleId), VehicleModel);
            preloader.resolve(RandomPaths.getTransactionsCountAdjustments(BARS_PER_DEAL.WITH_COUNTS), RandomModel);
          }

          if (buildPriceFilter.step === STEP_NUMBER.STEP_4 || buildPriceFilter.step === STEP_NUMBER.STEP_5) {
            // STEP 4, 5
            preloader.resolve(
              BuildPricePaths.buildConfigurationTmvWithOptionsPath(
                buildPriceFilter.styleId,
                buildPriceFilter.optionIds
              ),
              BuildPriceModel
            );
          }
        }

        const vehiclePageContext = modelState.get('vehicle', PageModel);
        setAdsListToPreload(preloader, store.getState(), [INCENTIVES_AD.AD_NAME], vehiclePageContext);

        // MENU, STEP 3
        preloader.resolve(
          getUnifiedLeadFormDealerCountV2Path({
            makeSlug: params.make,
            modelSlug: params.model,
            strategy: LEAD_FORM_CONFIGS.QUICK_QUOTES_ROI.strategy,
          }),
          UnifiedLeadFormModel
        );
        preloader.resolve(
          getUnifiedLeadFormDealerCountV2Path({
            makeSlug: params.make,
            modelSlug: params.model,
            strategy: LEAD_FORM_CONFIGS.QUICK_QUOTES_TRIM_DST.strategy,
          }),
          UnifiedLeadFormModel
        );

        return preloader.load().then(state => {
          const siteServedAds = state.get(
            getCreativeConfigAdPathByVehicle(mobile, vehiclePageContext),
            NativeAdsCreativeConfigModel
          );

          const mostPopularStyle = state.get(
            buildNoSubmodelVehiclePath({ make: params.make, model: params.model, year: currentModelYear.year }),
            VehicleModel
          );
          const leadFormDealerCount = state.get(
            getUnifiedLeadFormDealerCountV2Path({
              makeSlug: params.make,
              modelSlug: params.model,
              strategy: LEAD_FORM_CONFIGS.QUICK_QUOTES_TRIM_DST.strategy,
            }),
            UnifiedLeadFormModel
          );
          const hasLeadForm = !!((mostPopularStyle || buildPriceFilter.styleId) && leadFormDealerCount);

          // STEP 2.5
          const stylesList = state.get(
            BuildPricePaths.buildStylesListPath({
              make: params.make,
              model: params.model,
              submodel: makeNiceName(buildPriceFilter.styleFilter.submodel),
              year: currentModelYear.year,
            }),
            BuildPriceModel
          );

          // STEP 3, 4
          const optionsConfig = state.get(
            BuildPricePaths.buildConfigurationWithOptionsPath(buildPriceFilter.styleId, buildPriceFilter.optionIds),
            BuildPriceModel
          );
          if (buildPriceFilter.styleId) {
            buildPriceFilter.optionIds = optionsConfig.allOptions.toString();
            buildPriceFilter.urlOptionIds = without(optionsConfig.allOptions, ...optionsConfig.included).toString();
          }

          // STEP 4, 5
          const tmv = state.get(
            BuildPricePaths.buildConfigurationTmvWithOptionsPath(buildPriceFilter.styleId, buildPriceFilter.optionIds),
            BuildPriceModel
          );

          store.dispatch(
            setCriticalCss({
              hasLeadForm,
              ...siteServedAdsCriticalCss(siteServedAds),
              ...getStepCriticalCss({ step: buildPriceFilter.step, stylesList, optionsConfig, tmv }),
            })
          );

          // should be always the last line before final preloader.load() to avoid re-renders
          preloader.set(BuildPricePaths.getBuildPriceFilterPath(), BuildPriceModel, buildPriceFilter);
          return preloader.load();
        });
      })
      .catch(error => {
        if (error.status === 404) {
          if (error.redirectPageTo404) {
            throw error;
          }

          if (preloadStage === PRELOAD_STAGE.MODEL_YEAR_UNVALIDATED) {
            throw handle404Error({ error });
          }
        }

        logger.error(error, 'Build & Price preload failed', {
          make: params.make,
          model: params.model,
          modelyearId,
          styleId: buildPriceFilter.styleId,
          step: buildPriceFilter.step,
          preloadStage,
          sourceStatus: error.status,
        });
        throw handle500Error({ error });
      });
  },
  seo: (state, props) => {
    const params = getParamsWithMakes(props);
    const { defaultYear, defaultValue } = getQuery(props.location);
    const modelState = getModelState(state);
    const modelYears = modelState.get(BuildPricePaths.buildModelYearsPath(params), BuildPriceModel);
    const buildPriceFilter = modelState.get(BuildPricePaths.getBuildPriceFilterPath(), BuildPriceModel);
    const modelyearId = get(buildPriceFilter, 'styleFilter.modelyearId');
    const step = get(buildPriceFilter, 'step');
    const modelYear = Array.isArray(modelYears) && getModelYearById(modelYears, modelyearId);

    if (!modelYear) {
      return {
        robots: {
          noIndex: true,
        },
      };
    }

    const { makeName, modelName } = modelYear;

    const seoObject = {
      title: `${makeName} ${modelName} Build & Price - Build Your Own ${makeName}${get(
        STEP_TITLE_MAP,
        step,
        ''
      )} | Edmunds`,
      description: `Build a ${makeName} ${modelName} with Edmunds’ pricing tool. See immediate pricing breakdown as you build your own car. Check out Edmunds’ suggested builds and the most popular build for ${makeName} ${modelName}s.`,
      jsonld: [getBreadcrumbSchema(buildBreadcrumbs(modelYear))],
    };

    // Index only 1st step without defaultYear
    if (!(step === STEP_NUMBER.STEP_1 && !defaultYear && !defaultValue)) {
      seoObject.robots = {
        noIndex: true,
      };
    }

    return seoObject;
  },
  component: props => <BuildPrice {...props} params={getParamsWithMakes(props)} />,
  decorator: decoratorParams(EdmundsCpaNoAdhesionDecorator, { skipFloodlight: true }),
  options: {
    disableScrollToTop: true,
    hasVehicleContext: true,
    dontResetVehicleContextOnUnload: true,
  },
};

export default pageDefinition(pageOptions);
